import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import {
  Restaurant,
  CalendarToday,
  Scale,
  CheckCircle,
  Group,
  Close,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";

export interface CattleDetail {
  tag: string;
  name: string;
  _id: string;
  breed_code: string;
  gender: string;
  current_status: string;
  birth_date?: string | null;
  production_purpose?: string | null;
}

export interface NutritionData {
  api_message?: string;
  entity_type?: string;
  handling_type_code?: string;
  handling_date?: string;
  feed_type?: string;
  quantity_kg?: number;
  operation_status?: string;
  cattle_tag?: string[];
  lote_name?: string;
  cattle_with_details?: CattleDetail[];
  animals_found?: number;
  animals_not_found?: number;
  records_created?: number;
  related_entity?: {
    _id?: string;
    name?: string;
    cattle_tags?: string[];
    total_animals?: number;
    breed_code?: string;
    gender?: string;
  };
}

export interface NutritionViewData {
  data: NutritionData;
  message?: string;
  resumo?: string;
  url?: string;
}

interface NutritionViewProps {
  nutritionData?: NutritionViewData;
}

const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "completo":
      return DESIGN_TOKENS.colors.status.success;
    case "pendente":
      return DESIGN_TOKENS.colors.status.warning;
    case "erro":
      return DESIGN_TOKENS.colors.status.error;
    default:
      return DESIGN_TOKENS.colors.neutral[500];
  }
};

const formatDate = (dateString?: string, t?: any) => {
  if (!dateString) return t ? t('nutrition_view.date_not_informed') : t('nutrition_view.date_not_informed');
  try {
    // Se a data está no formato YYYY-MM-DD, adiciona horário para evitar problemas de timezone
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    // Para outros formatos, usa a conversão normal
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

export const NutritionView: React.FC<NutritionViewProps> = ({ nutritionData }) => {
  const { t } = useTranslation('animal');
  const [selectedAnimal, setSelectedAnimal] = useState<CattleDetail | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!nutritionData || !nutritionData.data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
          {t('nutrition_view.no_data')}
        </Typography>
      </Box>
    );
  }

  const { data } = nutritionData;
  const isLote = data.entity_type === 'lote';

  const handleAnimalClick = (animal: CattleDetail) => {
    setSelectedAnimal(animal);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAnimal(null);
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>

      {/* Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('nutrition_view.operation_status')}
            </Typography>
            <Chip
              icon={<CheckCircle />}
              label={data.operation_status || t('nutrition_view.complete')}
              color={data.operation_status?.toLowerCase() === "completo" ? "success" : "default"}
              sx={{
                bgcolor: getStatusColor(data.operation_status),
                color: "white",
                fontWeight: 600,
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
            {data.api_message || t('nutrition_view.operation_processed')}
          </Typography>
        </CardContent>
      </Card>

      {/* Nutrition Details */}
      <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
        {/* Feed Information */}
        <Box sx={{ flex: "1 1 300px", minWidth: 300 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <img 
                  src="/gestores/nutricao.svg" 
                  alt={t('nutrition_view.feed')} 
                  style={{ width: 24, height: 24 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('nutrition_view.feed_information')}
                </Typography>
              </Box>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                    {t('nutrition_view.feed_type')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {data.feed_type || t('nutrition_view.not_informed')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                    {t('nutrition_view.quantity_provided')}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Scale sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text.secondary }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.farm }}>
                      {data.quantity_kg ? `${data.quantity_kg} kg` : t('nutrition_view.not_informed')}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                    {t('nutrition_view.feeding_date')}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarToday sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text.secondary }} />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatDate(data.handling_date, t)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Entity Information */}
        <Box sx={{ flex: "1 1 300px", minWidth: 300 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                {isLote ? (
                  <Group sx={{ color: DESIGN_TOKENS.colors.primary[600] }} />
                ) : (
                  <img 
                    src="/vaca.svg" 
                    alt={t('nutrition_view.animal')} 
                    style={{ 
                      width: 20, 
                      height: 20
                    }} 
                  />
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('nutrition_view.entity_information', { 
                    entityType: isLote ? t('nutrition_view.lot') : t('nutrition_view.animal')
                  })}
                </Typography>
              </Box>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                    {t('nutrition_view.name')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.primary[600] }}>
                    {data.lote_name || data.related_entity?.name || t('nutrition_view.not_informed')}
                  </Typography>
                </Box>
                
                {isLote ? (
                  <>
                    <Box>
                      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                        {t('nutrition_view.total_animals')}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <img 
                          src="/vaca.png" 
                          alt={t('nutrition_view.animal')} 
                          style={{ width: 20, height: 20, filter: 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(102%) contrast(102%)' }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.farm }}>
                          {data.related_entity?.total_animals || data.cattle_tag?.length || t('nutrition_view.not_informed')} {t('nutrition_view.animals')}
                        </Typography>
                      </Box>
                    </Box>
                    {data.records_created && (
                      <Box>
                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                          {t('nutrition_view.records_created')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.status.success }}>
                          {data.records_created} {t('nutrition_view.records')}
                        </Typography>
                      </Box>
                    )}
                    
                    {data.animals_not_found !== undefined && data.animals_not_found > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                          {t('nutrition_view.animals_not_found')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.status.warning }}>
                          {data.animals_not_found} {t('nutrition_view.animals')}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Box>
                      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                        {t('nutrition_view.animal_tag')}
                      </Typography>
                      <Chip
                        label={data.cattle_tag || t('nutrition_view.not_informed')}
                        size="small"
                        sx={{
                          bgcolor: DESIGN_TOKENS.colors.neutral[100],
                          color: DESIGN_TOKENS.colors.text.primary,
                          fontFamily: "monospace",
                        }}
                      />
                    </Box>
                    {data.related_entity?.breed_code && (
                      <Box>
                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                          {t('nutrition_view.breed')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                          {data.related_entity.breed_code}
                        </Typography>
                      </Box>
                    )}
                    {data.related_entity?.gender && (
                      <Box>
                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5 }}>
                          {t('nutrition_view.gender')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {data.related_entity.gender}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Animals List */}
      {data.cattle_with_details && data.cattle_with_details.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t('nutrition_view.lot_animals', { count: data.cattle_with_details.length })}
            </Typography>
            <Box 
              sx={{ 
                maxHeight: 200, 
                overflow: "auto",
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                borderRadius: 1,
                p: 2,
                backgroundColor: DESIGN_TOKENS.colors.background.elevated,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                 {data.cattle_with_details.map((animal, index) => (
                   <Button
                     key={animal._id || index}
                     variant="outlined"
                     size="small"
                     onClick={() => handleAnimalClick(animal)}
                     sx={{
                       minWidth: "auto",
                       px: 2,
                       py: 0.5,
                       fontSize: "0.8rem",
                       fontFamily: "monospace",
                       fontWeight: 600,
                       borderRadius: 3,
                       backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                       borderColor: DESIGN_TOKENS.colors.primary[300],
                       color: DESIGN_TOKENS.colors.primary[700],
                       textTransform: "none",
                       transition: "all 0.2s ease",
                       '&:hover': {
                         backgroundColor: DESIGN_TOKENS.colors.primary[100],
                         borderColor: DESIGN_TOKENS.colors.primary[500],
                         transform: "translateY(-1px)",
                         boxShadow: `0 2px 8px ${DESIGN_TOKENS.colors.primary[200]}`,
                       }
                     }}
                   >
                     {animal.name || animal.tag}
                   </Button>
                 ))}
               </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Additional Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {t('nutrition_view.operation_details')}
          </Typography>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                {t('nutrition_view.entity_type')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                {data.entity_type || t('nutrition_view.not_informed')}
              </Typography>
            </Box>
            <Box sx={{ flex: "1 1 200px" }}>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                {t('nutrition_view.handling_code')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {data.handling_type_code?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || t('nutrition_view.not_informed')}
              </Typography>
            </Box>
            {data.related_entity?._id && (
              <Box sx={{ flex: "1 1 100%", mt: 1 }}>
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  {t('nutrition_view.related_entity_id')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                  {data.related_entity._id}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Animal Details Modal */}
      <Dialog 
        open={isModalOpen} 
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.neutral[300]}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          pb: 1,
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('nutrition_view.animal_details', { name: selectedAnimal?.name || selectedAnimal?.tag })}
          </Typography>
          <IconButton 
            onClick={handleCloseModal}
            size="small"
            sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {selectedAnimal && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Tag */}
              <Card sx={{ 
                backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1, fontSize: "0.8rem" }}>
                    🏷️ {t('nutrition_view.tag_label')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                    {selectedAnimal.tag}
                  </Typography>
                </CardContent>
              </Card>
              
              {/* ID */}
              <Card sx={{ 
                backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1, fontSize: "0.8rem" }}>
                    🆔 {t('nutrition_view.identification')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: "monospace", fontSize: "0.9rem" }}>
                    {selectedAnimal._id}
                  </Typography>
                </CardContent>
              </Card>
              
              {/* Info Grid */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Card sx={{ 
                  backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  borderRadius: 2
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1, fontSize: "0.8rem" }}>
                      🐄 {t('nutrition_view.breed_label')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                      {selectedAnimal.breed_code || t('nutrition_view.not_informed')}
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ 
                  backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  borderRadius: 2
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1, fontSize: "0.8rem" }}>
                      ⚥ {t('nutrition_view.gender_label')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedAnimal.gender || t('nutrition_view.not_informed')}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              
              {/* Status */}
              <Card sx={{ 
                backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                borderRadius: 2
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1, fontSize: "0.8rem" }}>
                    📊 {t('nutrition_view.current_status')}
                  </Typography>
                  <Chip
                    label={selectedAnimal.current_status || t('nutrition_view.not_informed')}
                    color={selectedAnimal.current_status === "active" ? "success" : "default"}
                    sx={{ 
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      height: 28
                    }}
                  />
                </CardContent>
              </Card>
              
              {/* Optional Fields */}
              {(selectedAnimal.birth_date || selectedAnimal.production_purpose) && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {selectedAnimal.birth_date && (
                    <Card sx={{ 
                      backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                      border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                      borderRadius: 2
                    }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1, fontSize: "0.8rem" }}>
                          🎂 {t('nutrition_view.birth_date')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formatDate(selectedAnimal.birth_date, t)}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                  
                  {selectedAnimal.production_purpose && (
                    <Card sx={{ 
                      backgroundColor: DESIGN_TOKENS.colors.background.elevated,
                      border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                      borderRadius: 2
                    }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1, fontSize: "0.8rem" }}>
                          🎯 {t('nutrition_view.production_purpose')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                          {selectedAnimal.production_purpose}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseModal}
            variant="contained"
            sx={{
              bgcolor: DESIGN_TOKENS.colors.primary[600],
              '&:hover': {
                bgcolor: DESIGN_TOKENS.colors.primary[700],
              },
              borderRadius: 2,
              px: 3
            }}
          >
            {t('nutrition_view.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NutritionView;